import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fleet, type CommandBatch, type CommandLogRow } from "@/api/client";
import { PageHead } from "@/components/PageHead";
import { DataTable, type DataCol } from "@/components/DataTable";
import { Select, SearchInput } from "@/components/inputs";
import { ActionRow } from "@/components/ActionRow";
import { Icon } from "@/components/icons";
import { DevicePicker } from "@/modules/asset_data/picker";
import { AssetDetail } from "@/modules/fleet/AssetDetail";
import { BulkPanel } from "@/modules/fleet/BulkPanel";
import { ACT_LABEL, COMMANDS, LEVEL_LABEL, STATE_MEANS, StateChip,
         actionClass, reasonText, toneVar } from "@/modules/fleet/catalogue";
import { CAP, useSession } from "@/auth/session";
import { POLL, ago } from "@/lib/format";

/** Fleet — one page: choose a command, choose who it applies to, run it.
 *
 *  WHY ONE PAGE. It was two subpages, Commands and Asset Control, and
 *  Dattatray rejected that: *"Asset control subpage doe snot make any
 *  sesnse!"* He was right, and the reason is the one that killed the split —
 *  he had just asked for the target selector to have **two parts, a single
 *  asset with the same filters and a bulk apply**. Once the command page can
 *  target one asset, a separate page for one asset is a second way to do one
 *  thing. D-101's rule, applied the other direction: one decision, one place.
 *
 *  THE ORDER IS THE FIX TO THE OLD COMMAND BOX, which he also called out as
 *  "entirely incoorect". It used to show an "Immobilise as" dropdown
 *  unconditionally — present and meaningless when you intended to mobilise —
 *  next to four controls with no primary among them. Now the COMMAND is
 *  chosen first, and everything downstream follows from its catalogue entry:
 *  its options appear only if it has any, and there is exactly ONE action
 *  button, carrying that command's label, colour and icon.
 *
 *  POLL CADENCE: the per-asset view and the batch rollups are operational
 *  state (POLL.STATE); the fleet-wide log is a list a human reads
 *  (POLL.LIST). Budgeting them differently is the UI performance law, not a
 *  preference — two 2 s polls on one page is how the console earned it.
 */

const SINGLE = "single";
const BULK = "bulk";

export function FleetPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const qc = useQueryClient();
  const mayCommand = can(CAP.IMMOBILIZE);

  //: WHICH COMMAND. Everything below reads from this entry rather than
  //: deciding for itself, so a new catalogue row needs no edit here.
  const [cmdKey, setCmdKey] = useState("immobilize");
  const spec = COMMANDS.find((c) => c.key === cmdKey) ?? COMMANDS[0];

  //: WHO. Exactly one target is live at a time — deliberately not two panels
  //: both holding a selection, because then the button's blast radius depends
  //: on which one you last touched. The console has shipped that bug before
  //: (one element driving two forms, D-84's catalogue).
  const [target, setTarget] = useState<typeof SINGLE | typeof BULK>(SINGLE);

  const [assetId, setAssetId] = useState("");
  //: the picker's own selection. A hold belongs to the ASSET (D-35: it
  //: outlives the box), so this only drives the picker and warns when a
  //: search named a stream with no asset behind it.
  const [stream, setStream] = useState("");
  const [action, setAction] = useState<number>(spec.options[0]?.action ?? 0);
  const [note, setNote] = useState("");
  //: "" means "let the server pick the default for this action" — a sentinel
  //: rather than a number, so the UI never has to restate 24h/15min as its
  //: own constant and drift from the server's.
  const [lifeMin, setLifeMin] = useState("");

  // the chosen command's own default restraint, whenever the command changes
  useEffect(() => { setAction(spec.options[0]?.action ?? 0); }, [cmdKey]);

  // `?asset=` is how the fleet map hands an asset over, the same shape the
  // Window and History pages use for `?imei=`.
  const deep = params.get("asset");
  useEffect(() => {
    if (!deep) return;
    setAssetId(deep);
    setTarget(SINGLE);
    setParams((prev) => { const p = new URLSearchParams(prev); p.delete("asset"); return p; },
              { replace: true });
  }, [deep]);

  const q = useQuery({
    queryKey: ["fleet", "asset", assetId],
    queryFn: () => fleet.asset(assetId),
    enabled: target === SINGLE && assetId.trim().length > 0,
    refetchInterval: POLL.STATE,
  });
  const batches = useQuery({
    queryKey: ["fleet", "batches"],
    queryFn: () => fleet.batches(),
    refetchInterval: POLL.STATE,
  });
  const log = useQuery({
    queryKey: ["fleet", "log"],
    queryFn: () => fleet.commands(),
    refetchInterval: POLL.LIST,
  });

  // STEP-UP. The server answers 428 STEP_UP_REQUIRED, which is a PROMPT and
  // not a failure — the client can fix it by asking for a code, which is
  // exactly why the status is 428 and not 403. Shown inline rather than as a
  // modal: an operator who has just chosen an asset should not lose it.
  const [needsStepUp, setNeedsStepUp] = useState(false);
  const [code, setCode] = useState("");
  const stepUp = useMutation({
    mutationFn: () => fleet.stepUp(code.trim()),
    onSuccess: () => { setNeedsStepUp(false); setCode(""); },
  });

  const act = useMutation({
    mutationFn: () => {
      const life = lifeMin ? Number(lifeMin) : undefined;
      return cmdKey === "immobilize"
        ? fleet.immobilize(assetId, {
            action, note: note || undefined, lifetime_minutes: life })
        : fleet.mobilize(assetId, undefined, life);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fleet", "asset", assetId] }),
    onError: (e: unknown) => {
      // 428 is the only error worth translating into UI state.
      if (String((e as Error)?.message ?? "").includes("STEP_UP_REQUIRED")) {
        setNeedsStepUp(true);
      }
    },
  });

  const v = q.data;

  return (
    <div className="mpage on">
      <PageHead eyebrow="Fleet · Commands" title="Command the fleet"
        desc={<>Choose a command, choose who it applies to, then run it. A
          command is not in force until the device says <b>applied</b> — one it
          is merely holding has stopped nothing yet.</>} />

      <div style={{ display: "flex", gap: 14, alignItems: "flex-start",
                    flexWrap: "wrap" }}>
        {/* 1 — WHICH COMMAND. The catalogue, rendered; not hand-built buttons. */}
        <div style={{ flex: "1 1 240px", maxWidth: 300, minWidth: 230 }}>
          <div className="section-label flush">Command</div>
          <div className="panel" style={{ overflow: "hidden" }}>
            {COMMANDS.map((c) => {
              const chosen = c.key === cmdKey;
              return (
                <button key={c.key} type="button" disabled={!c.built}
                  onClick={() => setCmdKey(c.key)}
                  title={c.built ? undefined : `${c.label} is not built yet`}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    padding: "12px 14px", border: 0,
                    borderTop: "1px solid var(--line)",
                    // TONE, not a literal: Mobilise highlighted red read as a
                    // warning about freeing a vehicle.
                    borderLeft: chosen ? `3px solid ${toneVar(c)}`
                                       : "3px solid transparent",
                    background: chosen
                      ? `color-mix(in srgb, ${toneVar(c)} 8%, transparent)`
                      : "transparent",
                    cursor: c.built ? "pointer" : "not-allowed",
                    opacity: c.built ? 1 : 0.55, font: "inherit",
                  }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 7,
                                 fontSize: 13.5, fontWeight: 700 }}>
                    <Icon name={c.icon} /> {c.label}
                  </span>
                  <span style={{ display: "block", fontSize: 11.5,
                                 color: "var(--t-mute)", marginTop: 2 }}>
                    {c.hint}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 2 — WHO IT APPLIES TO: one asset, or many. */}
        <div style={{ flex: "999 1 560px", minWidth: 0 }}>
          <div className="section-label flush">Who it applies to</div>
          <div className="panel panel-pad">
            <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              <TargetTab on={target === SINGLE} onClick={() => setTarget(SINGLE)}
                label="One asset" />
              {/* A one-shot command is single-only, so the bulk tab is simply
                  not offered for it rather than offered and refused. */}
              {spec.scope === "both" ? (
                <TargetTab on={target === BULK} onClick={() => setTarget(BULK)}
                  label="Bulk apply" />
              ) : (
                <TargetTab on={false} disabled
                  label="Bulk apply — not for this command" />
              )}
            </div>

            {target === SINGLE ? (
              <>
                {/* THE SAME PICKER the Live Window, History and the map use.
                    Three hand-built selectors for one question is how they
                    drift apart. */}
                <DevicePicker imei={stream} onPick={setStream}
                  onPickAsset={(a) => setAssetId(a ?? "")} />
                <div style={{ display: "flex", gap: 10, alignItems: "flex-end",
                              marginTop: 12, flexWrap: "wrap" }}>
                  <div>
                    <label className="simlab">Asset (VIN / BIN)</label>
                    {/* Authoritative and editable: the picker writes into it, a
                        deep link from the map fills it, and a VIN can still be
                        pasted. */}
                    <SearchInput mono style={{ width: 280 }}
                      placeholder="DRILL-ASSET-001" value={assetId}
                      onChange={(e) => setAssetId(e.target.value)} />
                  </div>
                  {/* OPTIONS ONLY IF THIS COMMAND HAS ANY. The old box showed
                      "Immobilise as" even when mobilising, which is a control
                      that cannot affect the outcome. */}
                  {spec.options.length > 0 && (
                    <div>
                      <label className="simlab">{spec.label} as</label>
                      <Select style={{ width: 210 }} value={action}
                        onChange={(e) => setAction(Number(e.target.value))}>
                        {spec.options.map((o) => (
                          <option key={o.action} value={o.action}>{o.label}</option>
                        ))}
                      </Select>
                    </div>
                  )}
                  {/* COMMAND LIFETIME (OFL-06's "configurable TTL"). The
                      service has taken a `lifetime` since the MVP and nothing
                      ever passed one, so "configurable" was satisfied one
                      layer below every caller — which is the same as not
                      being satisfied.

                      LABELLED FOR WHAT IT DOES. It is NOT how long the
                      restraint lasts: a hold lives until it is released
                      (OFL-06c), and the MVP wire format carries no `exp`, so
                      this value never reaches the device. Calling it
                      "lifetime" in the UI would tell an operator a locked
                      vehicle frees itself. */}
                  <div>
                    <label className="simlab">Retry delivery for</label>
                    <Select style={{ width: 170 }} value={lifeMin}
                      onChange={(e) => setLifeMin(e.target.value)}>
                      <option value="">
                        Default ({spec.key === "mobilize" ? "15 min" : "24 h"})
                      </option>
                      <option value="5">5 minutes</option>
                      <option value="15">15 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="360">6 hours</option>
                      <option value="1440">24 hours</option>
                      <option value="4320">3 days</option>
                      <option value="10080">7 days</option>
                    </Select>
                  </div>
                  <div>
                    <label className="simlab">Note (optional)</label>
                    <SearchInput style={{ width: 200 }} placeholder="why"
                      value={note} onChange={(e) => setNote(e.target.value)} />
                  </div>
                </div>
                {stream && !assetId && (
                  <div style={{ fontSize: 11.5, color: "var(--warn)", marginTop: 8 }}>
                    That search names a data stream, not an asset. Search by
                    Asset to place a hold — a hold belongs to the asset, not to
                    the box.
                  </div>
                )}

                {/* The write control sits behind CAP.IMMOBILIZE, not behind the
                    module gate: opening Fleet is a read, stopping somebody's
                    vehicle is not. */}
                <ActionRow>
                  {!mayCommand && (
                    <span style={{ fontSize: 11.5, color: "var(--t-mute)",
                                   marginRight: "auto" }}>
                      You can view an asset's state. Sending a command needs the
                      immobilise permission.
                    </span>
                  )}
                  {mayCommand && act.isError && !needsStepUp && (
                    <span style={{ fontSize: 11.5, color: "var(--bad)",
                                   marginRight: "auto" }}>
                      {String((act.error as Error)?.message ?? "command failed")}
                    </span>
                  )}
                  {/* ONE BUTTON, for the command you chose. Label, colour and
                      icon all come from its catalogue entry. */}
                  <button className={actionClass(spec, true)}
                    disabled={!assetId || !mayCommand || act.isPending}
                    onClick={() => act.mutate()}>
                    <Icon name={spec.icon} /> {spec.label}
                  </button>
                </ActionRow>
              </>
            ) : (
              <BulkPanel spec={spec} mayCommand={mayCommand} />
            )}
          </div>
        </div>
      </div>

      {needsStepUp && (
        <div className="panel panel-pad" style={{ marginTop: 14,
              borderLeft: "3px solid var(--warn)" }}>
          <b style={{ color: "var(--warn)" }}>Confirm it is you</b>
          <div style={{ fontSize: 12.5, marginTop: 4 }}>
            Immobilising needs your authenticator code. It stays valid for five
            minutes, so a bulk run does not ask again per asset.
            {" "}<b>Releasing a vehicle never needs this.</b>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end",
                        marginTop: 10 }}>
            <div>
              <label className="simlab">6-digit code or backup code</label>
              <SearchInput mono style={{ width: 180 }} value={code}
                autoComplete="one-time-code"
                onChange={(e) => setCode(e.target.value)} />
            </div>
            <button className="btn teal" disabled={!code.trim() || stepUp.isPending}
              onClick={() => stepUp.mutate()}>Confirm</button>
            {stepUp.isError && (
              <span style={{ fontSize: 11.5, color: "var(--bad)" }}>
                That code was not accepted.
              </span>
            )}
          </div>
        </div>
      )}

      {/* 3 — THE ASSET ITSELF, once one is chosen. */}
      {target === SINGLE && !assetId && (
        <div className="panel panel-pad cap" style={{ marginTop: 14 }}>
          Choose an asset above, or click one on the{" "}
          <Link to="/asset-data/overview">Overview map</Link>.
        </div>
      )}
      {target === SINGLE && assetId && q.isError && (
        <div className="panel panel-pad" style={{ marginTop: 14,
              borderLeft: "3px solid var(--bad)" }}>
          No such asset, or it is not yours.
        </div>
      )}
      {target === SINGLE && v && <AssetDetail v={v} />}

      <div className="section-label">Batches</div>
      <BatchTable rows={batches.data?.batches ?? []} loading={batches.isLoading} />

      <div className="section-label">All commands — newest first</div>
      <LogTable rows={log.data?.commands ?? []} loading={log.isLoading}
        hasMore={log.data?.has_more ?? false} onPick={(a) => {
          setTarget(SINGLE); setAssetId(a);
        }} />
    </div>
  );
}

function TargetTab({ on, label, onClick, disabled = false }: {
  on: boolean; label: string; onClick?: () => void; disabled?: boolean;
}) {
  return (
    <button type="button" disabled={disabled} onClick={onClick}
      style={{
        font: "inherit", fontSize: 12.5, fontWeight: on ? 700 : 500,
        padding: "7px 14px", borderRadius: 9, cursor: disabled ? "not-allowed" : "pointer",
        border: `1px solid ${on ? "var(--accent, var(--ok))" : "var(--line)"}`,
        background: on ? "color-mix(in srgb, var(--ok) 10%, transparent)" : "transparent",
        color: disabled ? "var(--t-mute)" : "inherit",
        opacity: disabled ? 0.6 : 1,
      }}>
      {label}
    </button>
  );
}

function BatchTable({ rows, loading }: { rows: CommandBatch[]; loading: boolean }) {
  const cols = useMemo<DataCol<CommandBatch>[]>(() => [
    { key: "when", label: "Started", width: 90, render: (b) => ago(b.created_at) },
    { key: "cmd", label: "Command", width: 180,
      render: (b) => `${b.type === "immobilize" ? "Immobilise" : "Mobilise"} · ${
        ACT_LABEL[b.action] ?? b.action}` },
    { key: "by", label: "By", width: 150, mono: true, render: (b) => b.created_by },
    { key: "level", label: "Level", width: 110,
      render: (b) => b.level != null ? (LEVEL_LABEL[b.level] ?? `level ${b.level}`) : "—" },
    // REQUESTED AND COMMANDED ARE SHOWN APART, frozen as of the run. A single
    // merged number would hide that assets were skipped.
    { key: "size", label: "Commanded", width: 120,
      title: (b) => `${b.requested} requested, ${b.skipped} skipped`,
      render: (b) => <>{b.commanded}<span className="muted">
        {" "}of {b.requested}</span></> },
    { key: "applied", label: "Applied", width: 95,
      render: (b) => b.applied > 0
        ? <StateChip state="applied" />
        : <span className="muted">—</span> },
    { key: "armed", label: "Waiting for halt", width: 130,
      title: () => STATE_MEANS.received,
      render: (b) => b.armed > 0
        ? <span className="chip" style={{ color: "var(--warn)",
            background: "color-mix(in srgb, var(--warn) 13%, transparent)" }}>
            {b.armed} armed</span>
        : <span className="muted">—</span> },
    { key: "refused", label: "Refused", width: 95,
      render: (b) => b.refused > 0
        ? <span className="chip" style={{ color: "var(--bad)",
            background: "color-mix(in srgb, var(--bad) 13%, transparent)" }}>
            {b.refused}</span>
        : <span className="muted">—</span> },
    { key: "done", label: "Run", width: 95,
      render: (b) => b.finished
        ? <span className="muted">finished</span>
        : <b style={{ color: "var(--warn)" }}>open</b> },
    { key: "batch", label: "Batch", width: 90, mono: true,
      title: (b) => b.batch_id, render: (b) => b.batch_id.slice(0, 8) },
  ], []);

  if (!loading && rows.length === 0) {
    return (
      <div className="panel panel-pad cap">
        No bulk runs yet. A run appears here the moment it starts, and stays
        <b> open</b> until no command under it can change.
      </div>
    );
  }
  return <DataTable rows={rows} cols={cols} rowKey={(b) => b.batch_id} />;
}

function LogTable({ rows, loading, hasMore, onPick }: {
  rows: CommandLogRow[]; loading: boolean; hasMore: boolean;
  onPick: (assetId: string) => void;
}) {
  const cols = useMemo<DataCol<CommandLogRow>[]>(() => [
    { key: "when", label: "When", width: 85, render: (c) => ago(c.created_at) },
    { key: "asset", label: "Asset", width: 180, mono: true,
      title: (c) => c.asset_id ?? c.connection_id,
      // SELECTS IT ON THIS PAGE rather than navigating: the asset's detail
      // appears above, which is the whole point of one page.
      render: (c) => c.asset_id
        ? <button type="button" onClick={() => onPick(c.asset_id as string)}
            style={{ font: "inherit", background: "none", border: 0, padding: 0,
                     color: "var(--link, var(--ok))", cursor: "pointer",
                     textDecoration: "underline" }}>{c.asset_id}</button>
        // NO ASSET MEANS THE BOX WAS COMMANDED BEFORE ITS ASSET WAS KNOWN.
        // Showing the IMEI rather than a blank keeps the row useful instead
        // of looking like missing data.
        : <span title="commanded by connection — no asset recorded">
            {c.connection_id}</span> },
    { key: "cmd", label: "Command", width: 185,
      render: (c) => `${c.type === "immobilize" ? "Immobilise" : "Mobilise"} · ${
        ACT_LABEL[c.action] ?? c.action}` },
    { key: "level", label: "Level", width: 110,
      render: (c) => c.level != null ? (LEVEL_LABEL[c.level] ?? `level ${c.level}`) : "—" },
    { key: "state", label: "State", width: 105,
      render: (c) => <StateChip state={c.state} /> },
    // THE "MEANS" COLUMN IS THE POINT. "expired_unapplied" tells an operator
    // nothing; "device had it and gave up" tells them what to do next.
    { key: "means", label: "Means", width: 240,
      title: (c) => STATE_MEANS[c.state] ?? c.state,
      render: (c) => <span style={{ fontSize: 12 }}>{STATE_MEANS[c.state] ?? "—"}</span> },
    { key: "reason", label: "Reason", width: 140,
      render: (c) => reasonText(c.reason_code) },
    { key: "batch", label: "Batch", width: 85, mono: true,
      title: (c) => c.batch_id ?? "single command",
      render: (c) => c.batch_id
        ? c.batch_id.slice(0, 8)
        : <span className="muted">—</span> },
  ], [onPick]);

  if (!loading && rows.length === 0) {
    return <div className="panel panel-pad cap">No commands recorded yet.</div>;
  }
  return (
    <>
      <DataTable rows={rows} cols={cols} rowKey={(c) => c.cmd_id} pageSize={25} />
      {hasMore && (
        <div style={{ fontSize: 11.5, color: "var(--t-mute)", margin: "8px 2px 0" }}>
          Showing the most recent {rows.length}. This log is a live view, not an
          archive.
        </div>
      )}
    </>
  );
}
